import { AFFILIATE_PARAMS, CLICK_ID_ONLY_PARAMS, UTM_PARAMS } from './campaign'

/**
 * Carries campaign parameters onto the links that leave this MFE for Portal.
 *
 * This exists because the `__ggUtmz` cookie bridge (FAAS-782) only works if it wins a
 * race, and it routinely loses. Portal reads the cookie at
 * `Web_Plugin_GoogleAnalytics::preDispatch` behind a guard:
 *
 *     } elseif (empty(App::session("campaign")->utm_campaign) && $utmz_cookie = ...)
 *
 * so it is consulted **only while the Portal session campaign is still empty**. Accepting
 * the OneTrust banner on any parameter-less Portal page makes Portal's own client-side
 * writer set `__ggUtmz` to the literal `(direct)`, the next Portal request latches that
 * into the session through the bare-string branch, and from then on the guard is false
 * forever. A correct cookie written later by this MFE is never read. Verified in
 * production: a PRD SIM order recorded `(direct)` on a Bing paid-search landing.
 *
 * A URL parameter has no such guard. The `ggCampaign` and `utm_campaign` branches sit
 * above it and call `App::session('campaign', true)`, whose `true` is `$cleanSession` and
 * does `Zend_Session::namespaceUnset` first. So a parameter overwrites whatever is latched,
 * every time, which is the property the cookie lacks.
 *
 * Applied to the DOM rather than at each call site on purpose. The federated CommonHeader
 * and CommonFooter also link into Portal and are rendered by a remote this repo cannot
 * touch, so a per-component helper could never reach them.
 */

/**
 * Portal's `validCampaign()` character allowlist, mirrored verbatim from
 * `GoogleAnalytics.php:16`.
 *
 * Notable absences: `$ * ; < = > @ \ ^ { | } ~`, backtick, and everything non-ASCII.
 * Microsoft Ads ValueTrack placeholders are `{...}` and multi-segment campaign names
 * commonly use `|`, so this is not a theoretical concern.
 */
const PORTAL_VALID_CHARACTERS = new Set(
  ' !"#%&\'()+,-./0123456789:?ABCDEFGHIJKLMNOPQRSTUVWXYZ[]_abcdefghijklmnopqrstuvwxyz'
)

/**
 * The ten parameters `validCampaign()` checks (`GoogleAnalytics.php:128-139`).
 *
 * `AFFILIATE_PARAMS` is already exactly that list minus the two UTM entries, so it is
 * reused rather than restated — a second copy would drift.
 */
const PORTAL_VALIDATED_PARAMS = new Set<string>([...AFFILIATE_PARAMS, 'utm_campaign', 'utm_medium'])

/**
 * Everything worth carrying across. Wider than the ten Portal validates, because the
 * point is to look like a direct Portal landing and a direct landing carries the lot.
 * The extras (`utm_id`, `utm_term`, `utm_content`, `msclkid`) are inert in Portal's
 * session but are read downstream by GTM and GA4.
 */
const FORWARDED_PARAMS: readonly string[] = [...UTM_PARAMS, ...AFFILIATE_PARAMS, ...CLICK_ID_ONLY_PARAMS]

/**
 * Strips characters Portal's allowlist rejects.
 *
 * Applied only to the ten parameters `validCampaign()` inspects, and it changes nothing
 * about what Portal ends up storing: `filterCampaign()` (`GoogleAnalytics.php:107-114`)
 * runs the identical `preg_replace` on the way into the session. The only thing
 * `validCampaign()` adds on top is the punishment — on a single bad character it calls
 * `Zend_Session::namespaceUnset('campaign')` and redirects to `/`.
 *
 * So sanitising here produces the same stored value while removing the bounce. That
 * matters more on a link than it did on the landing redirect: a visitor thrown back to the
 * homepage every time they click a CTA is a broken journey, not just lost attribution.
 */
const sanitiseForPortal = (value: string): string =>
  [...value].filter((character) => PORTAL_VALID_CHARACTERS.has(character)).join('')

/**
 * Builds the query string to carry, from the landing URL's search.
 *
 * Returns an empty string when there is nothing to carry, which is the signal for every
 * caller to do nothing at all. Ordinary non-campaign traffic never has a link touched.
 */
export function buildCampaignQuery(search: string): string {
  const from = new URLSearchParams(search)
  const carried = new URLSearchParams()

  for (const key of FORWARDED_PARAMS) {
    const value = from.get(key)

    if (value === null || value === '') {
      continue
    }

    const resolved = PORTAL_VALIDATED_PARAMS.has(key) ? sanitiseForPortal(value) : value

    // A validated parameter can sanitise down to nothing (a campaign name that was
    // entirely non-ASCII). Carrying an empty one would be worse than carrying none:
    // `is_string('')` is true in PHP, so `?ggCampaign=` takes Portal's first branch and
    // sets the campaign to the bare `-lastclick` suffix, beating the branches below it.
    if (resolved === '') {
      continue
    }

    carried.set(key, resolved)
  }

  return carried.toString()
}

/**
 * Merges the campaign query into one href.
 *
 * Returns the href unchanged when it is not ours to decorate — a different origin, a
 * fragment, a `mailto:`/`tel:` scheme — or when it already carries campaign parameters of
 * its own, which the Black Friday hero variant does.
 *
 * Preserves the shape it was given: a root-relative href stays root-relative, so the
 * rendered markup does not suddenly sprout absolute URLs.
 */
export function appendCampaignQuery(href: string, campaignQuery: string, origin: string): string {
  if (!campaignQuery || !href || href.startsWith('#')) {
    return href
  }

  let url: URL

  try {
    url = new URL(href, origin)
  } catch {
    return href
  }

  // Same-origin only. Portal and this MFE share an origin in every deployed environment;
  // community.giffgaff.com, help.giffgaff.com and the app store links do not, and have no
  // business receiving campaign parameters.
  if (url.origin !== origin) {
    return href
  }

  const carried = new URLSearchParams(campaignQuery)
  let changed = false

  carried.forEach((value, key) => {
    // Never overwrite a parameter the link set deliberately.
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
      changed = true
    }
  })

  if (!changed) {
    return href
  }

  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')

  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
}

/**
 * Rewrites every same-origin anchor under `root` to carry the campaign query.
 *
 * Idempotent, because `appendCampaignQuery` leaves an already-decorated href alone. That
 * is what makes it safe to call repeatedly from a MutationObserver.
 *
 * Rewrites the `href` attribute rather than intercepting clicks, so middle-click,
 * open-in-new-tab and copy-link-address all carry the campaign too. A click handler would
 * silently drop all three.
 *
 * @returns how many hrefs changed, which is what the tests assert on.
 */
export function decorateCampaignLinks(campaignQuery: string, root: ParentNode, origin: string): number {
  if (!campaignQuery) {
    return 0
  }

  let decorated = 0

  root.querySelectorAll('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href')

    if (!href) {
      return
    }

    const next = appendCampaignQuery(href, campaignQuery, origin)

    if (next !== href) {
      anchor.setAttribute('href', next)
      decorated += 1
    }
  })

  return decorated
}
